<template>
    <v-dialog
        v-model="showDialog"
        width="500"
        scrollable
        :fullscreen="this.$vuetify.breakpoint.xsOnly"
    >
        <v-card id="response-dialog">
            <v-card-title>
                <v-icon
                    left
                    color="primary"
                >
                    reply
                </v-icon>
                Respond
            </v-card-title>
            <v-divider />
            <v-card-text 
                ref="cardText"
                class="pt-3"
            >
                <v-form
                    ref="form"
                    v-model="formValid"
                >
                    <div class="response-info-box mb-5">
                        {{ headerText }}
                    </div>
                    <div
                        class="member-table-label"
                    >
                        Respond for Members
                    </div>
                    <v-data-table
                        v-model="selectedMembers"
                        :headers="membersTableHeaders"
                        :items="members"
                        item-key="member"
                        show-select
                        hide-default-footer
                        class="mt-2 mb-6"
                        mobile-breakpoint="0"
                    >
                        <template 
                            v-if="singleTicketSelected"
                            v-slot:[`header.data-table-select`]
                        >
                            <v-simple-checkbox
                                :disabled="singleTicketSelected"
                                :value="singleTicketSelected"
                                on-icon="check_box"
                                indeterminate-icon="indeterminate_check_box"
                                off-icon="check_box_outline_blank"
                            />
                        </template>
                        <template
                            v-else
                            v-slot:[`header.data-table-select`]="{ on, props }"
                        >
                            <v-simple-checkbox
                                v-ripple
                                v-bind="props"
                                on-icon="check_box"
                                indeterminate-icon="indeterminate_check_box"
                                off-icon="check_box_outline_blank"
                                v-on="on"
                            />
                        </template>

                        <template 
                            v-if="singleTicketSelected"
                            v-slot:[`item.data-table-select`]
                        >
                            <v-simple-checkbox
                                :disabled="singleTicketSelected"
                                :value="singleTicketSelected"
                                on-icon="check_box"
                                off-icon="check_box_outline_blank"
                            />
                        </template>
                        <template
                            v-else
                            v-slot:[`item.data-table-select`]="{ isSelected, select }"
                        >
                            <v-simple-checkbox
                                v-ripple
                                :value="isSelected"
                                on-icon="check_box"
                                off-icon="check_box_outline_blank"
                                @input="select($event)"
                            />
                        </template>

                        <template v-slot:[`item.member`]="{item}">
                            {{ item.member }} ({{ item.tickets.length }})
                        </template>
                        <template v-slot:[`item.currentResponse`]="{ item }">
                            {{ currentResponse(item) }}
                        </template>
                    </v-data-table>

                    <v-select
                        v-model="selectedResponse"
                        :items="responseSelect"
                        item-text="text"
                        item-value="value"
                        outlined
                        label="Response"
                        :rules="selectedResponseRule"
                        @change="changeResponseCode"
                    >
                        <template v-slot:append>
                            <v-icon>
                                arrow_drop_down
                            </v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                            <div class="response-select-item">
                                {{ item.text }}
                            </div>
                        </template>
                    </v-select>

                    <!-- BEGIN Response Code Dependant Fields -->
                    <div
                        v-if="showResponseAttachmentsList"
                        class="mb-4"
                    >
                        <v-alert
                            v-if="attachmentSectionMessage"
                            dense
                            outlined
                            type="warning"
                            class="mb-3"
                        >
                            {{ attachmentSectionMessage }}
                        </v-alert>

                        <v-radio-group
                            v-if="selectedTargets.length > 1"
                            v-model="attachmentMode"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Apply same upload to all selected response targets"
                                value="all"
                            />
                            <v-radio
                                label="Upload separate attachments per response target"
                                value="perTarget"
                            />
                        </v-radio-group>

                        <div
                            v-if="selectedResponseRequiresAttachment"
                            class="text-caption font-weight-medium mb-2"
                        >
                            {{ attachmentValidationSummary }}
                        </div>

                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-left">
                                            Ticket
                                        </th>
                                        <th class="text-left">
                                            Code
                                        </th>
                                        <th class="text-left">
                                            Uploaded
                                        </th>
                                        <th class="text-left">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="target in selectedTargets"
                                        :key="target.targetKey"
                                    >
                                        <td>{{ formatAttachmentTargetLabel(target) }}</td>
                                        <td>{{ target.member_code }}</td>
                                        <td>{{ getUploadedRowidCount(target.targetKey) }}</td>
                                        <td>
                                            <span v-if="getUploadedRowidCount(target.targetKey) > 0">
                                                Ready
                                            </span>
                                            <span
                                                v-else-if="selectedResponseRequiresAttachment"
                                                class="red--text font-weight-medium"
                                            >
                                                Attachment required
                                            </span>
                                            <span v-else>
                                                Optional
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>

                        <v-file-input
                            v-if="attachmentMode === 'all'"
                            v-model="pendingFilesShared"
                            class="mt-3"
                            outlined
                            dense
                            multiple
                            show-size
                            :accept="acceptedAttachmentTypes"
                            label="Files to upload for all selected response targets"
                            :disabled="attachmentOfflineBlocked || isUploadingAttachments"
                        />

                        <div
                            v-if="attachmentMode === 'all'"
                            class="d-flex justify-end mt-2"
                        >
                            <v-btn
                                small
                                color="primary"
                                :loading="isUploadingAttachments"
                                :disabled="!pendingFilesShared.length || attachmentOfflineBlocked"
                                @click="uploadSharedAttachments"
                            >
                                Upload to All Selected Targets
                            </v-btn>
                        </div>

                        <div
                            v-if="attachmentMode === 'perTarget'"
                            class="mt-3"
                        >
                            <div
                                v-for="target in selectedTargets"
                                :key="`${target.targetKey}-uploader`"
                                class="mb-4"
                            >
                                <div class="text-caption font-weight-medium mb-1">
                                    {{ formatAttachmentTargetLabel(target) }} / {{ target.member_code }}
                                </div>
                                <v-file-input
                                    :value="pendingFilesByTarget[target.targetKey] || []"
                                    outlined
                                    dense
                                    multiple
                                    show-size
                                    :accept="acceptedAttachmentTypes"
                                    label="Files to upload"
                                    :disabled="attachmentOfflineBlocked || isUploadingAttachments"
                                    @change="setPendingFilesForTarget(target.targetKey, $event)"
                                />
                                <div class="d-flex justify-end mt-2">
                                    <v-btn
                                        small
                                        color="primary"
                                        :loading="isUploadingAttachments"
                                        :disabled="!hasPendingFiles(target.targetKey) || attachmentOfflineBlocked"
                                        @click="uploadTargetAttachments(target)"
                                    >
                                        Upload
                                    </v-btn>
                                </div>
                                <div
                                    v-if="uploadErrorsByTarget[target.targetKey]"
                                    class="red--text text-caption mt-1"
                                >
                                    {{ uploadErrorsByTarget[target.targetKey] }}
                                </div>
                            </div>
                        </div>

                        <div class="mt-3">
                            <div
                                v-for="target in selectedTargets"
                                :key="`${target.targetKey}-mapped`"
                                class="mb-3"
                            >
                                <div
                                    v-if="getAvailableFiles(target.targetKey).length > 0"
                                    class="mb-2"
                                >
                                    <div class="text-caption font-weight-medium mb-1">
                                        Existing attachments for {{ formatAttachmentTargetLabel(target) }} / {{ target.member_code }}
                                    </div>
                                    <v-chip
                                        v-for="file in getAvailableFiles(target.targetKey)"
                                        :key="`${target.targetKey}-existing-${file.rowid}`"
                                        small
                                        outlined
                                        class="mr-2 mb-2"
                                        :color="isExistingAttachmentSelected(target.targetKey, file.rowid) ? 'primary' : ''"
                                        @click="toggleExistingAttachment(target, file)"
                                    >
                                        {{ file.file_name || file.caption || file.rowid }}
                                    </v-chip>
                                </div>
                                <div
                                    v-if="getUploadedFiles(target.targetKey).length > 0"
                                    class="mt-2"
                                >
                                    <div class="text-caption font-weight-medium mb-1">
                                        Uploaded for {{ formatAttachmentTargetLabel(target) }} / {{ target.member_code }}
                                    </div>
                                    <v-chip
                                        v-for="file in getUploadedFiles(target.targetKey)"
                                        :key="`${target.targetKey}-${file.rowid}`"
                                        close
                                        small
                                        class="mr-2 mb-2"
                                        @click:close="removeUploadedFile(target.targetKey, file.rowid)"
                                    >
                                        {{ file.file_name || file.caption || file.rowid }}
                                    </v-chip>
                                </div>
                            </div>
                        </div>
                    </div>

                    <v-row>
                        <v-col
                            cols="12"
                        >
                            <v-text-field
                                v-if="showExcavRepName"
                                v-model="excavRepName"
                                outlined
                                label="Excavator Rep Name"
                                :rules="[requireExcavRepName ? v => !!v || 'Excavator Rep Name is required' : () => true]"
                            />
                        </v-col>

                        <v-col
                            cols="12"
                        >
                            <v-text-field
                                v-if="showExcavRepPhone"
                                v-model="excavRepPhone"
                                outlined
                                label="Excavator Rep Phone"
                                :rules="[requireExcavRepPhone ? v => !!v || 'Excavator Rep Phone is required' : () => true]"
                            />
                        </v-col>

                        <v-col
                            cols="12"
                        >
                            <v-menu
                                v-if="showExcavProposedDueDate"
                                id="excav-proposed-due-date"
                                ref="excavProposedDueDateMenu"
                                v-model="excavProposedDueDateMenu"
                                :close-on-content-click="false"
                                :return-value.sync="excavProposedDueDate"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="excavProposedDueDate"
                                        label="Proposed Due Date"
                                        append-icon="event"
                                        outlined
                                        readonly
                                        :rules="[requireExcavProposedDueDate ? v => !!v || 'Proposed Due Date is required' : () => true]"
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="excavProposedDueDate"
                                    no-title
                                    scrollable
                                >
                                    <v-spacer />
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="excavProposedDueDateMenu = false"
                                    >
                                        Cancel
                                    </v-btn>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="$refs.excavProposedDueDateMenu.save(excavProposedDueDate)"
                                    >
                                        OK
                                    </v-btn>
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col
                            v-if="showExcavDisccusedDateTime"
                            cols="6"
                        >
                            <v-menu
                                ref="excavDisccusedDateMenu"
                                v-model="excavDisccusedDateMenu"
                                :close-on-content-click="false"
                                :return-value.sync="excavDisccusedDate"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="excavDisccusedDate"
                                        label="Discussed Date"
                                        append-icon="event"
                                        outlined
                                        readonly
                                        :rules="[requireExcavDisccusedTime ? v => !!v || 'Discussed Date is required' : () => true]"
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="excavDisccusedDate"
                                    no-title
                                    scrollable
                                >
                                    <v-spacer />
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="excavDisccusedDateMenu = false"
                                    >
                                        Cancel
                                    </v-btn>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="$refs.excavDisccusedDateMenu.save(excavDisccusedDate)"
                                    >
                                        OK
                                    </v-btn>
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col
                            v-if="showExcavDisccusedDateTime"
                            cols="6"
                        >
                            <v-text-field
                                v-model="excavDisccusedTime"
                                outlined
                                label="Discussed Time"
                                type="time"
                                :rules="[requireExcavDisccusedTime ? v => !!v || 'Discussed Time is required' : () => true]" 
                            />                            
                        </v-col>
                    </v-row>

                    <v-textarea
                        v-if="showResponseComment"
                        v-model="responseComment"
                        outlined
                        label="Positive Response Comment"
                        :rules="requireResponseComment ? responseCommentRules : []"
                    />

                    <v-text-field
                        v-if="showResponseUrl && selectedTickets.length === 1"
                        v-model="responseUrl"
                        outlined
                        label="Response URL"
                        :rules="[validateUrl]"
                    />

                    <v-select
                        v-if="showResponseCircumstance"
                        v-model="selectResponseCircumstance"
                        :items="responseCircumstances"
                        item-text="text"
                        item-value="value"
                        outlined
                        label="Circumstance"
                        :rules="requireResponseCircumstance ? circumStanceRules : []"
                    />

                    <v-text-field
                        v-if="showResponseMeetName"
                        v-model="meetName"
                        outlined
                        :label="centerId === 51 ? 'Excavator Rep':'Meet Excavator Name'"
                        :rules="requireResponseMeetName ? [v => (!!v || (centerId === 51 ? 'Excavator Rep is required':'Meet Excavator Name is required'))] : []"
                    />

                    <v-text-field
                        v-if="showResponseMeetPhone"
                        v-model="meetPhone"
                        outlined
                        label="Meet Phone Number"
                        :rules="requireResponseMeetPhone ? [v => (!!v || 'Meet Excavator Phone is required')] : []"
                    />

                    <v-row v-if="showResponseMeetDate">
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-menu
                                id="meet-date"
                                ref="meetDateMenu"
                                v-model="meetDateMenu"
                                :close-on-content-click="false"
                                :return-value.sync="meetDate"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="meetDate"
                                        label="Meet Date"
                                        append-icon="event"
                                        readonly
                                        v-bind="attrs"
                                        outlined
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="meetDate"
                                    no-title
                                    scrollable
                                >
                                    <v-spacer />
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="meetDateMenu = false"
                                    >
                                        Cancel
                                    </v-btn>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="$refs.meetDateMenu.save(meetDate)"
                                    >
                                        OK
                                    </v-btn>
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="meetTime"
                                outlined
                                label="Meet Time"
                                type="time"
                                :rules="requireResponseMeetDate ? [v => (!!v || 'Meet Time is required')]: []"
                            />                            
                        </v-col>
                    </v-row>                    

                    <v-text-field
                        v-if="showResponseMeetLocatorName"
                        v-model="meetLocatorName"
                        outlined
                        label="Meet Locator Name"
                        :rules="requireResponseMeetLocatorName ? [v => (!!v || 'Meet Locator Name is required')] : []"
                    />

                    <v-row v-if="showFutureLocateDate">
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-menu
                                id="future-locate-date"
                                ref="futureLocateDateMenu"
                                v-model="futureLocateDateMenu"
                                :close-on-content-click="false"
                                :return-value.sync="futureLocateDate"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="futureLocateDate"
                                        label="Future Locate Date"
                                        append-icon="event"
                                        readonly
                                        v-bind="attrs"
                                        outlined
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="futureLocateDate"
                                    no-title
                                    scrollable
                                >
                                    <v-spacer />
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="futureLocateDateMenu = false"
                                    >
                                        Cancel
                                    </v-btn>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="$refs.futureLocateDateMenu.save(futureLocateDate)"
                                    >
                                        OK
                                    </v-btn>
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="futureLocateTime"
                                outlined
                                label="Future Locate Time"
                                type="time"
                                :rules="requireFutureLocateDate ? [v => (!!v || 'Future Locate Time is required')]: []"
                            />                            
                        </v-col>
                    </v-row>

                    <v-text-field
                        v-if="showAttemptContactName"
                        v-model="attemptContactName"
                        outlined
                        label="Attempted Contact Name"
                        :rules="requireAttemptContactName ? [v => (!!v || 'Attempted Contact Name is required')] : []"
                    />

                    <v-row v-if="showAttemptContactDate">
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-menu
                                id="attempt-contact-date"
                                ref="attemptContactDateMenu"
                                v-model="attemptContactDateMenu"
                                :close-on-content-click="false"
                                :return-value.sync="attemptContactDate"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="attemptContactDate"
                                        label="Attempted Contact Date"
                                        append-icon="event"
                                        readonly
                                        v-bind="attrs"
                                        outlined
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="attemptContactDate"
                                    no-title
                                    scrollable
                                >
                                    <v-spacer />
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="attemptContactDateMenu = false"
                                    >
                                        Cancel
                                    </v-btn>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="$refs.attemptContactDateMenu.save(attemptContactDate)"
                                    >
                                        OK
                                    </v-btn>
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="attemptContactTime"
                                outlined
                                label="Contact Time"
                                type="time"
                                :rules="requireAttemptContactDate ? [v => (!!v || 'Attempted Contact Time is required')]: []"
                            />                            
                        </v-col>
                    </v-row>

                    <v-select
                        v-if="showAttemptContactMethod"
                        v-model="selectAttemptContactMethod"
                        :items="responseAttemptContactMethods"
                        item-text="text"
                        item-value="value"
                        outlined
                        label="Attempted Contact Method"
                        :rules="requireAttemptContactMethod ? attemptContactMethodRules : []"
                    />

                    <v-text-field
                        v-if="showAttemptContactPhoneOrEmail"
                        v-model="attemptContactPhoneOrEmail"
                        outlined
                        label="Attempted Contact Phone or Email"
                        :rules="requireAttemptContactPhoneOrEmail ? [v => (!!v || 'Attempted Contact Phone or Email is required')] : []"
                    />

                    <v-text-field
                        v-if="showLocatorName"
                        v-model="locatorName"
                        outlined
                        label="Locator Name"
                        :rules="requireLocatorName ? [v => (!!v || 'Locator Name is required')] : []"
                    />

                    <v-text-field
                        v-if="showLocatorPhoneOrEmail"
                        v-model="locatorPhoneOrEmail"
                        outlined
                        label="Locator Phone Or Email"
                        :rules="requireLocatorPhoneOrEmail ? [v => (!!v || 'Locator Phone or Email is required')] : []"
                    />

                    <v-row v-if="showProposedCompletionDate">
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-menu
                                id="proposed-completion-date"
                                ref="proposedCompletionDateMenu"
                                v-model="proposedCompletionDateMenu"
                                :close-on-content-click="false"
                                :return-value.sync="proposedCompletionDate"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="proposedCompletionDate"
                                        label="Proposed Completion Date"
                                        append-icon="event"
                                        readonly
                                        v-bind="attrs"
                                        outlined
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="proposedCompletionDate"
                                    no-title
                                    scrollable
                                >
                                    <v-spacer />
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="proposedCompletionDateMenu = false"
                                    >
                                        Cancel
                                    </v-btn>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="$refs.proposedCompletionDateMenu.save(proposedCompletionDate)"
                                    >
                                        OK
                                    </v-btn>
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="proposedCompletionTime"
                                outlined
                                label="Proposed Completion Time"
                                type="time"
                                :rules="requireProposedCompletionDate ? [v => (!!v || 'Proposed Completion Time is required')]: []"
                            />                            
                        </v-col>
                    </v-row>

                    <v-textarea
                        v-if="showMarkedAreaDescription"
                        v-model="markedAreaDescription"
                        outlined
                        label="Marked Area Description"
                        :rules="requireMarkedAreaDescription ? [v => (!!v || 'Marked Area Description is required')] : []"
                    />

                    <!-- END Response Code Dependant Fields -->

                    <v-row>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-menu
                                id="locate-date"
                                ref="locateDateMenu"
                                v-model="locateDateMenu"
                                :close-on-content-click="false"
                                :return-value.sync="locateDate"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="locateDate"
                                        label="Locate Date"
                                        append-icon="event"
                                        readonly
                                        v-bind="attrs"
                                        outlined
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="locateDate"
                                    no-title
                                    scrollable
                                >
                                    <v-spacer />
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="locateDateMenu = false"
                                    >
                                        Cancel
                                    </v-btn>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="$refs.locateDateMenu.save(locateDate)"
                                    >
                                        OK
                                    </v-btn>
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="locateTime"
                                outlined
                                label="Locate Time"
                                type="time"
                            />                            
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="unitsMarked"
                                outlined
                                label="Units Marked"
                                type="number"
                                :rules="[
                                    v => /^$|^\d+$/.test(v) || 'Please enter a positive number.'
                                ]"
                            />
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="timeWorked"
                                outlined
                                label="Time Worked #m or #h #m"
                                :rules="timeWorkedRules"
                            />
                        </v-col>
                    </v-row>
                    <v-divider class="mb-8" />
                    <TicketResponseBillingCode
                        v-if="hasBillingModule"
                        :billing-codes="billing"
                        @billing-codes="SaveBillingCodes"
                    />
                    <v-divider class="mb-8" />
                    <v-textarea
                        v-model="workDone"
                        outlined
                        label="Work Done Information"
                    />

                    <v-textarea
                        v-model="inhouseComments"
                        outlined
                        label="In-House Comments"
                        :rules="[
                            v => v.length <= responseCommentMaxLength || `In-House Comments must be less than ${responseCommentMaxLength} characters`
                        ]"
                    />

                    <v-textarea
                        v-if="hasExcavatorResponse"
                        v-model="noteToExcavator"
                        outlined
                        label="Note to Excavator"
                    />
                </v-form>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-spacer />
                <v-btn
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="cancel"
                >
                    Cancel
                </v-btn>
                <v-btn
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    :disabled="disableRespondButton"
                    depressed
                    color="primary"
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="respond"
                >
                    Respond
                </v-btn>
                <v-spacer v-if="this.$vuetify.breakpoint.xs" />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import moment from 'moment';
import { getCenterOption } from '../utils/options';
import { hasPermission } from '../utils/permissions';
import { htmlEncode } from '../utils/encoding';
import { csvContains } from '../utils/csvContains.js';
import { isSType } from '../services/ticketDataHelpers.js';
import webtmsBackend from '@/services/webtmsBackend';
import { isTruthy } from '@/utils/isTruthy';
import { guidEqual, guidNormal } from '@/utils/guid.js';
import TicketResponseBillingCode from './TicketDetails/ResponseBilling/TicketResponseBillingCode';
import CompanyBillingMixin from '@/mixins/CompanyBillingMixin';

export default {
    components: {
        TicketResponseBillingCode,
    },
    mixins: [
        CompanyBillingMixin
    ],
    props: {
        selectedTickets: {
            type: Array,
            default: () => ([]),
            required: true,
        },
        show: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        formValid: false,
        selectedMembers: [],
        showSelectMembersDialog: false,
        selectMembersDialogText: '',
        responseSelect: [],
        responseSelectPrompt: {
            value: '',
            text: 'Select'
        },
        selectedResponse: '',
        selectResponseCircumstance: null,
        responseCircumstances: [],
        responseComment: '',
        responseUrl: '',
        
        showExcavRepName: false,
        requireExcavRepName: false,
        excavRepName: '',
        showExcavRepPhone: false,
        requireExcavRepPhone: false,
        excavRepPhone: '',
        
        showExcavProposedDueDate: false,
        requireExcavProposedDueDate: false,
        excavProposedDueDate: null,
        excavProposedDueDateMenu: null,

        showExcavDisccusedDateTime: false,
        requireExcavDisccusedTime: false,
        excavDisccusedDate: null,
        excavDisccusedTime: null,
        excavDisccusedDateMenu: null,

        meetName: '',

        meetPhone: '',

        meetDate: null,
        meetTime: null,
        meetDateMenu: null,

        meetLocatorName: '',

        futureLocateDate: null,
        futureLocateDateMenu: null,
        futureLocateTime: null,

        attemptContactName: '',

        attemptContactDate: null,
        attemptContactTime: null,
        attemptContactDateMenu: null,

        selectAttemptContactMethod: [],
        responseAttemptContactMethods: ['Phone','E-Mail'],

        attemptContactPhoneOrEmail: '',

        locatorName: '',

        locatorPhoneOrEmail: '',

        proposedCompletionDate: null,
        proposedCompletionTime: null,
        proposedCompletionDateMenu: null,

        markedAreaDescription: '',

        locateDate: null,
        locateDateMenu: null,
        locateTime: null,

        unitsMarked: 0,
        timeWorked: '',

        workDone: '',
        inhouseComments: '',
        noteToExcavator: '',

        showResponseComment: false,
        showResponseUrl: false,
        showResponseMeetName: false,
        showResponseMeetPhone: false,
        showResponseMeetDate: false,
        showResponseMeetLocatorName: false,
        showResponseCircumstance: false,
        showFutureLocateDate: false,

        showAttemptContactName: false,
        showAttemptContactDate: false,
        showAttemptContactMethod: false,
        showAttemptContactPhoneOrEmail: false,
        showLocatorName: false,
        showLocatorPhoneOrEmail: false,
        showProposedCompletionDate: false,
        showMarkedAreaDescription: false,
        
        requireResponseComment: false,
        requireResponseUrl: false,
        requireResponseMeetName: false,
        requireResponseMeetPhone: false,
        requireResponseMeetDate: false,
        requireResponseMeetLocatorName: false,
        requireResponseCircumstance: false,
        requireFutureLocateDate: false,
        
        requireAttemptContactName: false,
        requireAttemptContactDate: false,
        requireAttemptContactMethod: false,
        requireAttemptContactPhoneOrEmail: false,
        requireLocatorName: false,
        requireLocatorPhoneOrEmail: false,
        requireProposedCompletionDate: false,
        requireMarkedAreaDescription: false,
        
        billing: [],
        urlRegEx: '^(https?:\\/\\/)' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-z\\d_]*)?$',
        addBillingCode: false,    
        timeWorkedRules: [
            v =>  { return /^$|^\d+$|^((\d+)h) ((\d+)m)$|^((\d+)h)$|^((\d+)m)$/.test(v) || 'Use "#" or "#h" or "#m" or "#h #m".'; },
        ],
        circumStanceRules: [
            v => (!!v || 'Circumstance is required'),
        ],
        attemptContactMethodRules: [
            v => (!!v || 'Attempted Contact Method is required'),
        ],
        billingCodeRules: [
            v => !!v || 'Billing Code is required',
            v => (v && v.length >= 4 && v.length <= 8) || 'Billing Code must be between 4 and 8 characters',
        ],
        responseCommentRules: [
            v => !!v || 'Response Comment is required',
            v => (v && v.length >= 10) || 'Response Comment must be at least 10 characters long'
        ],
        maxAttachmentSize: 41975808, // 40MB
        maxFilesPerTarget: 5,
        maxFilesPerDialogSession: 25,
        attachmentMode: 'perTarget',
        acceptedAttachmentTypes: '.pdf,.jpg,.jpeg,.png,.tiff,.tif,.txt,.heic,.heics,.gif',
        pendingFilesShared: [],
        pendingFilesByTarget: {},
        uploadedRowidsByTarget: {},
        uploadedFilesByTarget: {},
        availableFilesByTarget: {},
        uploadErrorsByTarget: {},
        attachmentValidationError: '',
        isUploadingAttachments: false,
        showResponseAttachmentsList: false,
    }),
    computed:{
        headerText() {
            if(this.selectedTickets.length) {
                if(this.sameTicketMultiSelected ) {
                    const ticket = this.firstSelectedTicket;
                    const ticketRevision = ticket.revision ? (ticket.ticket + '-' + ticket.revision) : ticket.ticket;
                    return `Ticket: ${ticketRevision} (${ticket.center}/${ticket.state})`;
                } else {
                    return `Respond to ${ this.selectedMembers.reduce(
                        (previousValue, currentValue) => {
                            return previousValue + currentValue.tickets.length;
                        },
                        0
                    ) } of ${ this.selectedTickets.length } Tickets Selected`;
                }
            }
            return '';
        },
        disableRespondButton() {
            if (!this.formValid || this.selectedMembers.length === 0) {
                return true;
            }
            if (this.attachmentOfflineBlocked) {
                return true;
            }
            return this.hasIncompleteRequiredAttachments;
        },
        selectedResponseRule() {
            return [
                this.selectedResponse.length > 0 || 'Please select a response code.'
            ];
        },
        showDialog: {
            get() {
                return this.show;                
            },
            set() {
                this.$emit('close-response-dialog');
            },
        },
        respondCount() {
            return 0;
        },
        members(){
            let keys = [];
            return this.selectedTickets.reduce(function(previousValue, currentValue) {
                let index = keys.indexOf(currentValue.TR_member_code);
                if(index === -1) {
                    previousValue.push ({
                        member: currentValue.TR_member_code,
                        tickets:[{
                            tid: currentValue.tid,
                            TR_response_code: currentValue.TR_response_code,
                        }],
                    });
                    keys.push(currentValue.TR_member_code);
                } else {
                    previousValue[index].tickets.push({
                        tid: currentValue.tid,
                        TR_response_code: currentValue.TR_response_code,
                    });
                }
                return previousValue;
            }, []);
        },
        sameTicketMultiSelected() {
            for(let ticket of this.selectedTickets) {
                if(!guidEqual(this.firstSelectedTicket.tid, ticket.tid)) {
                    return false;
                }
            }
            return true;
        },
        singleTicketSelected() {
            return this.selectedTickets.length===1;
        },
        membersTableHeaders(){
            let membersTableHeaders = [
                {
                    text: 'Members',
                    value: 'member'
                },
            ];
            if(this.showCurrentResponse) {
                membersTableHeaders.push({
                    text: 'Current Response',
                    value: 'currentResponse'
                });
            }
            return membersTableHeaders;
        },
        showCurrentResponse() {
            for(let member of this.members){
                if(member.tickets.length > 1) {
                    return false;
                }
            }
            return true;
        },
        firstSelectedTicket() {
            return this.selectedTickets[0];
        },
        centerId() {
            if(this.selectedTickets.length) {
                return this.firstSelectedTicket.center_id;
            }
            return undefined;
        },
        state() {
            if(this.selectedTickets.length) {
                return this.firstSelectedTicket.state;
            }
            return undefined;
        },
        hasExcavatorResponse() {
            return hasPermission('EXCAVATOR_RESPONSE');
        },
        hasBillingModule() {
            return hasPermission('USE_BILLING_MODULE');
        },   
        responseCommentMaxLength() {
            return getCenterOption(this.centerId, 'RESPONSE_COMMENT_MAXLEN');
        },
        selectedResponseRequiresAttachment() {
            if (!this.selectedResponse) {
                return false;
            }
            const { requiredFields } = this.getResponseCodeFields(this.selectedResponse);
            return requiredFields.includes('attachment');
        },
        selectedTargets() {
            const targets = [];
            this.selectedMembers.forEach(selected => {
                selected.tickets.forEach(memberTicket => {
                    const ticket = this.selectedTickets.find(currentTicket => {
                        return guidEqual(currentTicket.tid, memberTicket.tid)
                            && currentTicket.TR_member_code === selected.member;
                    });
                    if (!ticket) {
                        return;
                    }
                    targets.push({
                        tid: ticket.tid,
                        ticket: ticket.ticket,
                        revision: ticket.revision,
                        member_code: selected.member,
                        targetKey: this.buildAttachmentTargetKey(ticket.tid, selected.member),
                    });
                });
            });
            return targets;
        },
        attachmentOfflineBlocked() {
            return this.$store.getters.forceOffline && this.selectedResponseRequiresAttachment;
        },
        hasIncompleteRequiredAttachments() {
            if (!this.selectedResponseRequiresAttachment) {
                return false;
            }
            return this.selectedTargets.some(target => this.getUploadedRowidCount(target.targetKey) === 0);
        },
        attachmentValidationSummary() {
            if (!this.selectedResponseRequiresAttachment) {
                return '';
            }
            const missingCount = this.selectedTargets.filter(target => this.getUploadedRowidCount(target.targetKey) === 0).length;
            if (missingCount === 0) {
                return 'All selected response targets have attachments.';
            }
            if (missingCount === 1) {
                return '1 response target still needs an attachment.';
            }
            return `${missingCount} of ${this.selectedTargets.length} response targets still need attachments.`;
        },
        attachmentSectionMessage() {
            if (this.attachmentOfflineBlocked) {
                return 'Attachments are required for this response code and cannot be submitted while offline.';
            }
            if (this.hasUploadErrors) {
                return this.uploadErrorSummary;
            }
            if (this.selectedResponseRequiresAttachment && this.hasIncompleteRequiredAttachments) {
                return 'Attachment required for this response code.';
            }
            return this.attachmentValidationError;
        },
        hasUploadErrors() {
            return Object.keys(this.uploadErrorsByTarget).length > 0;
        },
        uploadErrorSummary() {
            const errorCount = Object.keys(this.uploadErrorsByTarget).length;
            if (errorCount === 1) {
                return '1 response target has an attachment upload error.';
            }
            return `${errorCount} response targets have attachment upload errors.`;
        },
    },
    watch: {
        async showDialog(show) {
            if(show) {
                await this.setControlDefaultValues();
            } else {
                this.$refs.cardText.scrollTop = 0;
                this.hideAllExtraResponseFields();
                this.clearAllFields();
                this.$refs.form.resetValidation();
            }
        },
        async selectedMembers() {
            if (!this.show || !this.showResponseAttachmentsList) {
                return;
            }
            await this.refreshSelectedTargetAttachments();
        },
    },
    mounted() {
        this.clearAllFields();
    },
    methods: {
        cancel() {
            this.showDialog=false;
            this.billing = [];
            this.$emit('cancel-response-dialog');
        },
        currentResponse(member) {
            if(this.showCurrentResponse){
                if(member?.tickets[0].TR_response_code?.length){
                    return member.tickets[0].TR_response_code;
                } else {
                    return 'No Responses';
                }
            }
            return '';
        },
        async respond() {
            if(this.selectedMembers.length === 0) {
                this.selectMembersDialogText = 'Please select one or more members to respond for.';
                this.showSelectMembersDialog = true;
                return;
            }
            if(!this.selectedResponse) {
                this.selectMembersDialogText = 'Please select a response code.';
                this.showSelectMembersDialog = true;
                return;
            }
            let dateLocated = this.locateDate + ' ' + this.locateTime;
            dateLocated = moment(dateLocated);
            let objPostResponseEx = {
                response_code: this.selectedResponse,
                date_located: dateLocated.toISOString(),
                units_marked: this.unitsMarked,
                units_worked: this.getUnitsWorkedValue(this.timeWorked),
                work_done: this.workDone.trim(),
                remarks: this.inhouseComments.trim(),
                excav_note: this.hasExcavatorResponse ? this.noteToExcavator.trim() : '',
                billing:this.billing ? this.billing : [] // billing codes
            };

            const { inputFields: fields, requiredFields } = this.getResponseCodeFields(this.selectedResponse);

            if (fields.includes('comment') || requiredFields.includes('comment')) {
                objPostResponseEx.response_comment = this.responseComment.trim();
            }
            if (fields.includes('url')) {
                objPostResponseEx.response_url = this.responseUrl.trim();
            }
            if (fields.indexOf('meet_name') >= 0) {
                objPostResponseEx.response_meet_name = this.meetName.trim();
            }
            if (fields.indexOf('meet_phone') >= 0) {
                objPostResponseEx.response_meet_phone = this.meetPhone.trim();
            }
            if (fields.indexOf('meet_date') >= 0) {
                let date = this.meetDate + ' ' + this.meetTime;
                // convert to ISO format
                objPostResponseEx.response_meet_date = (moment(date)).toISOString();
            }
            if (fields.indexOf('meet_locator_name') >= 0) {
                objPostResponseEx.response_meet_locator_name = this.meetLocatorName.trim();
            }
            if (fields.indexOf('circumstance') >= 0) {
                objPostResponseEx.response_circumstance = this.selectResponseCircumstance.trim();
            }
            if (fields.indexOf('future_locate_date') >= 0) {
                let date = this.futureLocateDate + ' ' + this.futureLocateTime;
                // convert to ISO format
                objPostResponseEx.response_future_locate_date = (moment(date)).toISOString();
            }
            if (fields.indexOf('attempt_contact_name') >= 0) {
                objPostResponseEx.response_attempt_contact_name = this.attemptContactName.trim();
            }
            if (fields.indexOf('attempt_contact_date') >= 0) {
                let date = this.attemptContactDate + ' ' + this.attemptContactTime;
                // convert to ISO format
                objPostResponseEx.response_attempt_contact_date = (moment(date)).toISOString();
            }
            if (fields.indexOf('attempt_contact_method') >= 0) {
                objPostResponseEx.response_attempt_contact_method = this.selectAttemptContactMethod.trim();
            }
            if (fields.indexOf('attempt_phone_or_email') >= 0) {
                objPostResponseEx.response_attempt_phone_or_email = this.attemptContactPhoneOrEmail.trim();
            }
            if (fields.indexOf('locator_name') >= 0) {
                objPostResponseEx.response_locator_name = this.locatorName.trim();
            }
            if (fields.indexOf('locator_phone_or_email') >= 0) {
                objPostResponseEx.response_locator_phone_or_email = this.locatorPhoneOrEmail.trim();
            }
            if(fields.indexOf('proposed_completion_date') >= 0) {
                let date = this.proposedCompletionDate + ' ' + this.proposedCompletionTime;
                // convert to ISO format
                objPostResponseEx.response_proposed_completion_date = (moment(date)).toISOString();
            }
            if (fields.indexOf('marked_area_description') >= 0) {
                objPostResponseEx.response_marked_area_description = this.markedAreaDescription.trim();
            }
            if (fields.indexOf('attachment') >= 0) {
                objPostResponseEx.response_attachment = this.selectedTargets.length === 1
                    ? this.getUploadedRowidsCsv(this.selectedTargets[0].targetKey)
                    : '';
            }
            if (fields.indexOf('excavator_rep_name') >= 0) {
                objPostResponseEx.response_excavator_rep_name = this.excavRepName.trim();
            }
            if (fields.indexOf('excavator_rep_phone') >= 0) {
                objPostResponseEx.response_excavator_rep_phone = this.excavRepPhone.trim();
            }
            if (fields.indexOf('proposed_due_date') >= 0) {
                let date = (moment(this.excavProposedDueDate)).format('YYYY-MM-DD');
                objPostResponseEx.response_proposed_due_date = date;
            }
            if (fields.indexOf('excavator_discussion_datetime') >= 0) {
                let date = `${this.excavDisccusedDate} ${this.excavDisccusedTime}`;
                objPostResponseEx.response_excavator_discussion_datetime = (moment(date)).toISOString();
            }
            if (this.$store.getters.forceOffline) {
                var saveTicketResponseMessage = {
                    handlerName: 'saveTicketResponse',
                    ticketResponse: objPostResponseEx
                };
                for(let selected of this.selectedMembers) {
                    for(let memberTicket of selected.tickets){
                        let ticket = this.selectedTickets.find(ticket => {
                            return guidEqual(ticket.tid, memberTicket.tid)
                            && ticket.TR_member_code === selected.member;
                        });
                        if(ticket) {
                            objPostResponseEx.response_date = (new Date()).toISOString();
                            objPostResponseEx.tid = ticket.tid;
                            objPostResponseEx.ticket = ticket.ticket;
                            objPostResponseEx.revision = ticket.revision;
                            objPostResponseEx.rowid = [ticket.TR_rowid];
                            objPostResponseEx.member_code = [ticket.TR_member_code];
                            objPostResponseEx.response_attachment = this.getUploadedRowidsCsv(
                                this.buildAttachmentTargetKey(ticket.tid, ticket.TR_member_code)
                            );
                            navigator.serviceWorker.controller.postMessage(saveTicketResponseMessage);
                        } else {
                            console.error('Cannot respond to ticket ', memberTicket.tid, ' because not found in selectedTickets ', this.selectedTickets);
                        }
                    }
                }
                this.$emit('offline-reponse-success');
            } else {
                objPostResponseEx.tickets = [];
                for(let selected of this.selectedMembers) {
                    for(let memberTicket of selected.tickets){
                        objPostResponseEx.tickets.push({
                            tid: memberTicket.tid,
                            member_code: selected.member,
                            response_attachment: this.getUploadedRowidsCsv(
                                this.buildAttachmentTargetKey(memberTicket.tid, selected.member)
                            ),
                        });
                    }
                }
                let postResponseResult = await webtmsBackend.postResponse(objPostResponseEx);
                // raise event for parent to indicate success
                this.$emit('reponse-success', postResponseResult);
            }
        },
        getUnitsWorkedValue(unitsWorked) {
            let hours = 0;
            let minutes = 0;
            let h;
            let m;
            var reHM = /^(\d+)[hH]\s(\d+)[mM]$/;
            var reH = /^(\d+)[hH]$/;
            var reM = /^(\d+)[mM]$/;
            var reNumberOnly = /^\d+$/;

            if (reNumberOnly.test(unitsWorked)) {
                minutes = Number(unitsWorked);
            }

            // like '#h'?
            h = reH.exec(unitsWorked);
            if (h && h.length === 2) {
                hours = Number(h[1]);
            }
            // like '#m'?
            m = reM.exec(unitsWorked);
            if (m && m.length === 2) {
                minutes = Number(m[1]);
            }
            // like '#h #m'?
            let hm = reHM.exec(unitsWorked);
            if (hm && hm.length === 3) {
                hours = Number(hm[1]);
                minutes = Number(hm[2]);
            }
            let units = (hours * 60) + minutes;
            return units;
        },
        getPositiveResponseMapping (code, centerId, state) {
            return this.$store.getters.companyPositiveResponseMap.find(map => {
                if(map.center_id === centerId
                && map.response_code === code
                && map.state === state) {
                    return true;
                } 
            });
        },
        getResponseCodeListForCenterState(centerId, state) {
            const centersPosRespCodes = this.$store.getters.centersPosRespCodes;
            let list = centersPosRespCodes.filter(el => {
                if (el.center_id === centerId && el.state === state)
                    return true;
            });
            // Exit in degenerate case
            if (list.length === 0) {
                return [];
            }
            let filteredCompanyResponseCodeList = [];
            // let companyResponseCodes = ;
            for (let code of this.$store.getters.companyResponseCodes) {
                code.mapping = this.getPositiveResponseMapping(code.response_code, centerId, state);
                if (!code.mapping) {
                    // no mapping for this code in this center/state
                    continue;
                }
                // exclude code if the mapping is marked as not valid for this state (mapping is '-x-')
                if (code.mapping.positive_response_code === '-x-') {
                    continue;
                }
                filteredCompanyResponseCodeList.push(code);
            }
            return this.sortResponseCodeList(filteredCompanyResponseCodeList);
        },
        sortResponseCodeList(responseCodes) {
            let r = responseCodes.sort((a, b) => {
                let aCode = a.response_code.replace(/[^0-9]/g, '');
                let bCode = b.response_code.replace(/[^0-9]/g, '');
                if (aCode === bCode) {
                    return a.response_code.localeCompare(b.response_code);
                }
                return aCode - bCode;
            });
            if(r[0].response_code === '---') {
                r.push(r.shift());
            }
            return r;
        },
        getSelectedTicketsCenterResponseCodes() {
            // NOTE: It's assumed that selectedTickets is not empty, and consists 
            // of tickets with the same center_id, state, style:design, stype:complex_project and stype:emergency (if we have any codes with allow_emergency=0)
            let firstSelectedTicket = this.firstSelectedTicket;
            if(!firstSelectedTicket) {
                console.error('this.selectedTickets is ', this.selectedTickets);
                return [];
            }
            const areCloseOnlyTickets = isSType('closeonly', firstSelectedTicket) || isSType('cancel', firstSelectedTicket);
            const areEmergencyTickets = isSType('emergency', firstSelectedTicket);
            const areDesignTickets = isSType('design', firstSelectedTicket);
            const areComplexProjectTickets = isSType('complex_project', firstSelectedTicket);
            const areComplexProjectMeetTickets = isSType('complex_project_meet', firstSelectedTicket);
            const areComplexMarkingDeviationTickets = isSType('complex_marking_deviation', firstSelectedTicket);
            const areAllSameEmergencyStatus = this.selectedTickets.every(ticket => isSType('emergency', ticket) === isSType('emergency', firstSelectedTicket));
            console.log('areAllSameEmergencyStatus', areAllSameEmergencyStatus);

            // returns an array of response codes available for the 
            // selected center_id, state and (areDesignTickets,areCloseOnlyTickets) ticket selection classification
            // If a +Response Map for a code maps to '-x-' then it will be excluded ('-x-' ==> Disabled: Response code not available for this state)
            let companyResponseCodes = this.getResponseCodeListForCenterState(this.centerId, this.state); // returns list that excludes any '-x-' code mappings

            let bHaveDesignCodes = !!companyResponseCodes.find(code => code.design);
            let bHaveComplexCodes = !!companyResponseCodes.find(code => code.complex_project);
            let bHaveComplexMeetCodes = !!companyResponseCodes.find(code => code.complex_project_meet);
            let bHaveComplexMarkingDeviationCodes = !!companyResponseCodes.find(code => code.complex_marking_deviation);

            let bDESIGN_TICKETS__ALLOW_ALL_RESPONSES = isTruthy(getCenterOption(this.centerId, 'DESIGN_TICKETS--ALLOW_ALL_RESPONSES'));
            let szDESIGN_TICKETS__DISABLED_RESPONSES = getCenterOption(this.centerId, 'DESIGN_TICKETS--DISABLED_RESPONSES');
            let bCOMPLEX_TICKETS__ALLOW_ALL_RESPONSES = isTruthy(getCenterOption(this.centerId, 'COMPLEX_TICKETS--ALLOW_ALL_RESPONSES'));
            let szCOMPLEX_TICKETS__DISABLED_RESPONSES = getCenterOption(this.centerId, 'COMPLEX_TICKETS--DISABLED_RESPONSES');
            let bCOMPLEX_MEET_TICKETS__ALLOW_ALL_RESPONSES = isTruthy(getCenterOption(this.centerId, 'COMPLEX_MEET_TICKETS--ALLOW_ALL_RESPONSES'));
            let szCOMPLEX_MEET_TICKETS__DISABLED_RESPONSES = getCenterOption(this.centerId, 'COMPLEX_MEET_TICKETS--DISABLED_RESPONSES');
            let bCOMPLEX_MARKING_DEVIATION_TICKETS__ALLOW_ALL_RESPONSES = isTruthy(getCenterOption(this.centerId, 'COMPLEX_MARKING_DEVIATION_TICKETS--ALLOW_ALL_RESPONSES'));
            let szCOMPLEX_MARKING_DEVIATION_TICKETS__DISABLED_RESPONSES = getCenterOption(this.centerId, 'COMPLEX_MARKING_DEVIATION_TICKETS--DISABLED_RESPONSES');

            // *** Functions for returning response codes
            let useCloseNoResponseCode = function useCloseNoResponseCode() {
                return [{ 
                    response_code: '---', 
                    description: 'CLOSE TICKET', 
                    completes: true, 
                    design: false, 
                    complex_project: false,
                    complex_project_meet: false,
                    allow_emergency: true,
                    send_pr_center: false, 
                    send_pr_excavator: false,
                    pr_excavator_text: '',
                    pr_excavator_voice: ''
                }];
            };
            let useDesignCodes = function useDesignCodes(allowAll, disabled_codes) {
                let list = [];
                if(typeof disabled_codes === 'string') {
                    disabled_codes = disabled_codes.replace(' ', '').trim();
                } else {
                    disabled_codes = '';
                }
                for (let i = 0; i < companyResponseCodes.length; i++) {
                    if (companyResponseCodes[i].design) {
                        list.push(companyResponseCodes[i]);
                        continue;
                    }
                    if (companyResponseCodes[i].complex_project) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_project_meet) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_marking_deviation) {
                        continue;
                    }
                    if (allowAll) {
                        if(csvContains(disabled_codes, companyResponseCodes[i].response_code)) {
                            continue;
                        }
                        list.push(companyResponseCodes[i]);
                    }
                }
                return list;
            };
            let useComplexProjectCodes = function useComplexProjectCodes(allowAll, disabled_codes) {
                let list = [];
                if(typeof disabled_codes === 'string') {
                    disabled_codes = disabled_codes.replace(' ', '').trim();
                } else {
                    disabled_codes = '';
                }
                for (let i = 0; i < companyResponseCodes.length; i++) {
                    if (companyResponseCodes[i].complex_project) {
                        list.push(companyResponseCodes[i]);
                        continue;
                    }
                    if (companyResponseCodes[i].design) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_project_meet) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_marking_deviation) {
                        continue;
                    }
                    if (allowAll) {
                        if(csvContains(disabled_codes, companyResponseCodes[i].response_code)) {
                            continue;
                        }
                        list.push(companyResponseCodes[i]);
                    }
                }
                return list;
            };
            let useComplexProjectMeetCodes = function useComplexProjectMeetCodes(allowAll, disabled_codes) {
                let list = [];
                if(typeof disabled_codes === 'string') {
                    disabled_codes = disabled_codes.replace(' ', '').trim();
                } else {
                    disabled_codes = '';
                }
                for (let i = 0; i < companyResponseCodes.length; i++) {
                    if (companyResponseCodes[i].complex_project_meet) {
                        list.push(companyResponseCodes[i]);
                        continue;
                    }
                    if (companyResponseCodes[i].design) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_project) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_marking_deviation) {
                        continue;
                    }
                    if (allowAll) {
                        if(csvContains(disabled_codes, companyResponseCodes[i].response_code)) {
                            continue;
                        }
                        list.push(companyResponseCodes[i]);
                    }
                }
                return list;
            };
            let useComplexMarkingDeviationCodes = function useComplexMarkingDeviation(allowAll, disabled_codes) {
                let list = [];
                if(typeof disabled_codes === 'string') {
                    disabled_codes = disabled_codes.replace(' ', '').trim();
                } else {
                    disabled_codes = '';
                }
                for (let i = 0; i < companyResponseCodes.length; i++) {
                    if (companyResponseCodes[i].complex_marking_deviation) {
                        list.push(companyResponseCodes[i]);
                        continue;
                    }
                    if (companyResponseCodes[i].design) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_project) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_project_meet) {
                        continue;
                    }
                    if (allowAll) {
                        if(csvContains(disabled_codes, companyResponseCodes[i].response_code)) {
                            continue;
                        }
                        list.push(companyResponseCodes[i]);
                    }
                }
                return list;
            };
            let useAllCodesNoDesignNoComplex = function useAllCodesNoDesignNoComplex() {
                let list = [];
                for (let i = 0; i < companyResponseCodes.length; i++) {
                    if(companyResponseCodes[i].design) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_project) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_project_meet) {
                        continue;
                    }
                    if (companyResponseCodes[i].complex_marking_deviation) {
                        continue;
                    }
                    if(companyResponseCodes[i].allow_emergency === false && areEmergencyTickets) {
                        continue;
                    }
                    list.push(companyResponseCodes[i]);
                }
                return list;
            };

            // *** Determine list of codes to return
            if (areCloseOnlyTickets) {
                return useCloseNoResponseCode();
            }

            // Handle Design/Engineering response types
            if (bHaveDesignCodes) {
                if (areDesignTickets) {
                    return useDesignCodes(bDESIGN_TICKETS__ALLOW_ALL_RESPONSES, szDESIGN_TICKETS__DISABLED_RESPONSES);
                }
            }
            // Handle Complex Project response types
            if(bHaveComplexCodes) {
                if(areComplexProjectTickets) {
                    return useComplexProjectCodes(bCOMPLEX_TICKETS__ALLOW_ALL_RESPONSES, szCOMPLEX_TICKETS__DISABLED_RESPONSES);
                }
            }
            // Handle Complex Project Meet response types
            if(bHaveComplexMeetCodes) {
                if(areComplexProjectMeetTickets) {
                    return useComplexProjectMeetCodes(bCOMPLEX_MEET_TICKETS__ALLOW_ALL_RESPONSES, szCOMPLEX_MEET_TICKETS__DISABLED_RESPONSES);
                }
            }
            // Handle Complex Marking Deviation response types
            if(bHaveComplexMarkingDeviationCodes) {
                if(areComplexMarkingDeviationTickets) {
                    // For Complex Marking Deviation tickets, use the Complex Project codes
                    return useComplexMarkingDeviationCodes(bCOMPLEX_MARKING_DEVIATION_TICKETS__ALLOW_ALL_RESPONSES, szCOMPLEX_MARKING_DEVIATION_TICKETS__DISABLED_RESPONSES);
                }
            }
            // default: all codes not design or complex types
            return useAllCodesNoDesignNoComplex();
        },
        isDayBeforeDueDateAndPast4pm(ticket) {
            if (!ticket || !ticket.duedate) return false;
            const dueDate = new Date(ticket.duedate);
            const today = new Date();

            // Get the day before due date
            const dayBeforeDue = new Date(dueDate);
            dayBeforeDue.setDate(dueDate.getDate() - 1);

            // Compare year, month, day
            const isDayBefore =
                today.getFullYear() === dayBeforeDue.getFullYear() &&
                today.getMonth() === dayBeforeDue.getMonth() &&
                today.getDate() === dayBeforeDue.getDate();

            // Check if current time is past 4pm
            const isPast4pm = today.getHours() > 16 || (today.getHours() === 16 && today.getMinutes() > 0);

            return isDayBefore && isPast4pm;
        },
        getFormattedResponseCodes() {
            const responseCodes = this.getSelectedTicketsCenterResponseCodes();
            const selectArray = responseCodes.map(code => {
                return {
                    value: htmlEncode(code.response_code),
                    text: htmlEncode(code.response_code === '---' ? code.description : code.response_code + ' - ' + code.description),
                    requires: code.mapping?.pr_required_fields?.length > 0 ? code.mapping.pr_required_fields.split(',') : [],
                };
            });
            const screenLogixArray = [];
            if(hasPermission('USE_SCREEN_LOGIX')) {
                screenLogixArray.push({
                    value: 'RELEASE',
                    text: 'RELEASE',
                });
            }
            this.responseSelect = [
                this.responseSelectPrompt,
                ...screenLogixArray,
                ...selectArray
            ];
            this.selectedTickets.forEach(ticket => {
                if(ticket.dueDateText?.toLowerCase() === 'overdue' || this.isDayBeforeDueDateAndPast4pm(ticket)) {
                    this.responseSelect = this.responseSelect.reduce((acc, obj) => {
                        if (obj.value !== '51') {
                            acc.push(obj);
                        }
                        return acc;
                    }, []);
                }
            });
            this.selectedResponse = '';
        },
        getResponseCodeFields(responseCode) {
            let result = {
                inputFields: [],
                requiredFields: [],
            };
            const posRespMap = this.getPositiveResponseMapping(responseCode, this.centerId, this.state);
            if (posRespMap) {
                // strip all spaces from string
                posRespMap.pr_input_fields = posRespMap.pr_input_fields.replace(/ /g, '');
                let inputFields = posRespMap.pr_input_fields.split(',');
                if (inputFields[0] === '') {
                    inputFields = [];
                }
                result.inputFields = inputFields;
                posRespMap.pr_required_fields = posRespMap.pr_required_fields.replace(/ /g, '');
                let requiredFields = posRespMap.pr_required_fields.split(',');
                if (requiredFields[0] === '') {
                    requiredFields = [];
                }
                result.requiredFields = requiredFields;
            }
            return result;
        },
        hideAllExtraResponseFields() {
            // Hide all extra response fields
            this.showResponseComment = false;
            this.showResponseUrl = false;
            this.showResponseMeetName = false;
            this.showResponseMeetPhone = false;
            this.showResponseMeetDate = false;
            this.showResponseMeetLocatorName = false;
            this.showResponseCircumstance = false;
            this.responseCircumstances = [];
            this.showFutureLocateDate = false;
            this.showAttemptContactName = false;
            this.showAttemptContactDate = false;
            this.showAttemptContactMethod = false;
            this.showAttemptContactPhoneOrEmail = false;
            this.showLocatorName = false;
            this.showLocatorPhoneOrEmail = false;
            this.showProposedCompletionDate = false;
            this.showMarkedAreaDescription = false;
            this.showResponseAttachmentsList = false;
            this.showExcavRepName = false;
            this.showExcavRepPhone = false;
        },
        changeResponseCode(responseCode) {
            const hadAttachmentInputs = this.showResponseAttachmentsList;
            const {inputFields, requiredFields} = this.getResponseCodeFields(responseCode);

            this.hideAllExtraResponseFields();
            
            console.log(`>> Response Code: ${responseCode}  Input Fields: [${inputFields}]  Required FIelds: [${requiredFields}]`);

            for(let field of inputFields) {
                switch(field) {
                    case 'comment':
                        this.showResponseComment = true;
                        break;
                    case 'url':
                        this.showResponseUrl = true;
                        break;
                    case 'circumstance':
                        this.showResponseCircumstance = true;
                        this.loadResponseCircumstances();
                        break;
                    case 'future_locate_date':
                        this.showFutureLocateDate = true;
                        break;
                    case 'meet_date':
                        this.showResponseMeetDate = true;
                        break;
                    case 'meet_phone':
                        this.showResponseMeetPhone = true;
                        break;
                    case 'meet_name':
                        this.showResponseMeetName = true;
                        break;
                    case 'meet_locator_name':
                        this.showResponseMeetLocatorName = true;
                        break;
                    case 'attempt_contact_name':
                        this.showAttemptContactName = true;
                        break;
                    case 'attempt_contact_date':
                        this.showAttemptContactDate = true;
                        break;
                    case 'attempt_contact_method':
                        this.showAttemptContactMethod = true;
                        break;
                    case 'attempt_phone_or_email':
                        this.showAttemptContactPhoneOrEmail = true;
                        break;
                    case 'locator_name':
                        this.showLocatorName = true;
                        break;
                    case 'locator_phone_or_email':
                        this.showLocatorPhoneOrEmail = true;
                        break;
                    case 'proposed_completion_date':
                        this.showProposedCompletionDate = true;
                        break;
                    case 'marked_area_description':
                        this.showMarkedAreaDescription = true;
                        break;
                    case 'attachment':
                        this.showResponseAttachmentsList = true;
                        break;
                    case 'excavator_rep_name':
                        this.showExcavRepName = true;
                        this.requireExcavRepName = false;
                        break;
                    case 'excavator_rep_phone':
                        this.showExcavRepPhone = true;
                        this.requireExcavRepPhone = false;
                        break;
                    case 'proposed_due_date':
                        this.showExcavProposedDueDate = true;
                        this.requireExcavProposedDueDate = false;
                        break;
                    case 'excavator_discussion_datetime':
                        this.showExcavDisccusedDateTime = true;
                        this.requireExcavDisccusedTime = false;
                        break;
                    default:
                        break;
                }
            }

            this.requireResponseComment = false;
            this.requireResponseUrl = false;
            this.requireResponseMeetName = false;
            this.requireResponseMeetPhone = false;
            this.requireResponseMeetDate = false;
            this.requireResponseMeetLocatorName = false;
            this.requireResponseCircumstance = false;
            this.requireFutureLocateDate = false;
            this.requirefutureLocateTime = false;
            this.requireAttemptContactName = false;
            this.requireAttemptContactDate = false;
            this.requireAttemptContactMethod = false;
            this.requireAttemptContactPhoneOrEmail = false;
            this.requireLocatorName = false;
            this.requireLocatorPhoneOrEmail = false;
            this.requireProposedCompletionDate = false;
            this.requireMarkedAreaDescription = false;

            for(let field of requiredFields) {
                switch(field) {
                    case 'comment':
                        this.requireResponseComment = true;
                        break;
                    case 'url':
                        this.requireResponseUrl = true;
                        break;
                    case 'circumstance':
                        this.requireResponseCircumstance = true;
                        break;
                    case 'future_locate_date':
                        this.requireFutureLocateDate = true;
                        break;
                    case 'meet_date':
                        this.requireResponseMeetDate = true;
                        break;
                    case 'meet_phone':
                        this.requireResponseMeetPhone = true;
                        break;
                    case 'meet_name':
                        this.requireResponseMeetName = true;
                        break;
                    case 'meet_locator_name':
                        this.requireResponseMeetLocatorName = true;
                        break;
                    case 'attempt_contact_name':
                        this.requireAttemptContactName = true;
                        break;
                    case 'attempt_contact_date':
                        this.requireAttemptContactDate = true;
                        break;
                    case 'attempt_contact_method':
                        this.requireAttemptContactMethod = true;
                        break;
                    case 'attempt_phone_or_email':
                        this.requireAttemptContactPhoneOrEmail = true;
                        break;
                    case 'locator_name':
                        this.requireLocatorName = true;
                        break;
                    case 'locator_phone_or_email':
                        this.requireLocatorPhoneOrEmail = true;
                        break;
                    case 'proposed_completion_date':
                        this.requireProposedCompletionDate = true;
                        break;
                    case 'marked_area_description':
                        this.requireMarkedAreaDescription = true;
                        break;
                    case 'attachment':
                        this.showResponseAttachmentsList = true;
                        break;
                    case 'excavator_rep_name':
                        this.showExcavRepName = true;
                        this.requireExcavRepName = true;
                        break;
                    case 'excavator_rep_phone':
                        this.showExcavRepPhone = true;
                        this.requireExcavRepPhone = true;
                        break;
                    case 'proposed_due_date':
                        this.showExcavProposedDueDate = true;
                        this.requireExcavProposedDueDate = true;
                        break;
                    case 'excavator_discussion_datetime':
                        this.showExcavDisccusedDateTime = true;
                        this.requireExcavDisccusedTime = true;
                        break;
                    default:
                        break;
                }
            }
            if (hadAttachmentInputs && !this.showResponseAttachmentsList) {
                this.clearPendingAttachmentSelections();
            }
            if (this.showResponseAttachmentsList) {
                this.refreshSelectedTargetAttachments().catch(error => {
                    this.attachmentValidationError = error?.message || 'Failed to load existing attachments.';
                });
            }
            this.setExtraControlDefaultValues(responseCode);
        },
        loadResponseCircumstances() {
            this.responseCircumstances = [];
            let responseCircumstanceValues = getCenterOption(this.centerId, 'RESPONSE_CIRCUMSTANCE_VALUES');
            if(responseCircumstanceValues) {
                this.responseCircumstances = responseCircumstanceValues.split(',');
            }
        },
        getCurrentDate(date = new Date()) {            
            return date.toISOString().slice(0, 10);
        },
        getCurrentTime(date = new Date()) {
            const options = {
                hour12: false,
                hour: '2-digit', 
                minute: '2-digit'
            };
            return date.toLocaleTimeString('en-US', options);
        },
        async setControlDefaultValues() {
            const now = new Date();
            this.locateDate = this.getCurrentDate(now);
            this.locateTime = this.getCurrentTime(now);
            this.getFormattedResponseCodes();
            if(this.selectedTickets.length === 1
            && this.members.length) {
                this.selectedMembers = [this.members[0]];
                const ticket = this.firstSelectedTicket;
                // check for offline response
                // if offline response exists load the offline response Data
                let offlineResponse = this.$store.getters.offlineResponses.find(response => guidEqual(response.rowid, ticket.TR_rowid));
                if (offlineResponse) {
                    await this.loadCurrentResponse(offlineResponse);
                } else if (ticket.TR_response_code.length) {
                    const currentResponse = await webtmsBackend.getTicketResponse(ticket.TR_rowid);
                    await this.loadCurrentResponse(currentResponse);
                }
            }
        },
        async loadCurrentResponse(currentResponse) {
            this.selectedResponse = currentResponse.response_code;
            this.responseComment = currentResponse.response_comment;
            this.responseUrl = currentResponse.response_url;
            this.selectResponseCircumstance = currentResponse.response_circumstance;
            this.meetName = currentResponse.response_meet_name;
            this.meetPhone = currentResponse.response_meet_phone;
            this.meetDate = currentResponse.response_meet_date ? this.getCurrentDate(new Date(currentResponse.response_meet_date)) : null;
            this.meetLocatorName = currentResponse.response_meet_locator_name;
            this.futureLocateDate = currentResponse.response_future_locate_date ? this.getCurrentDate(new Date(currentResponse.response_future_locate_date)) : null;
            this.attemptContactName = currentResponse.response_attempt_contact_name;
            this.attemptContactDate = currentResponse.response_attempt_contact_date ? this.getCurrentDate(new Date(currentResponse.response_attempt_contact_date)) : null;
            this.attemptContactTime = currentResponse.response_attempt_contact_date ? this.getCurrentTime(new Date(currentResponse.response_attempt_contact_date)) : null;
            this.selectAttemptContactMethod = currentResponse.response_attempt_contact_method;
            this.attemptContactPhoneOrEmail = currentResponse.response_attempt_phone_or_email;
            this.locatorName = currentResponse.response_locator_name;
            this.locatorPhoneOrEmail = currentResponse.response_locator_phone_or_email;
            this.proposedCompletionDate = currentResponse.response_proposed_completion_date ? this.getCurrentDate(new Date(currentResponse.response_proposed_completion_date)) : null;
            this.proposedCompletionTime = currentResponse.response_proposed_completion_date ? this.getCurrentTime(new Date(currentResponse.response_proposed_completion_date)) : null;
            this.markedAreaDescription = currentResponse.response_marked_area_description;
            this.unitsMarked = currentResponse.units_marked;
            this.timeWorked = this.formatCurrentUnitsWorked(currentResponse.units_worked_count);
            this.workDone = currentResponse.work_done;
            this.inhouseComments = currentResponse.remarks;
            this.noteToExcavator = currentResponse.excav_note;
            if(this.selectedResponse.length > 0) {
                this.changeResponseCode(this.selectedResponse);
            }
            if (currentResponse.response_attachment?.length > 0 && this.selectedTargets.length === 1) {
                const target = this.selectedTargets[0];
                const rowids = currentResponse.response_attachment
                    .split(',')
                    .map(rowid => guidNormal(rowid))
                    .filter(Boolean);
                this.uploadedRowidsByTarget = {
                    ...this.uploadedRowidsByTarget,
                    [target.targetKey]: rowids,
                };
                await this.refreshUploadedFilesForTarget(target);
            }
        },
        formatCurrentUnitsWorked(unitsWorked) {
            const minutes = unitsWorked % 60;
            const hours = Math.floor(unitsWorked/60);
            let result = '';
            if(hours) {
                result = hours + 'h';
            }
            if(minutes) {
                if(hours) {
                    result += ' ';
                }
                result += minutes + 'm';
            }
            return result;
        },
        setExtraControlDefaultValues(rcode) {
            const now = new Date();
            if(this.showResponseMeetDate) {
                this.meetDate = this.getCurrentDate(now);
                this.meetTime = this.getCurrentTime(now);
            }
            if(this.showFutureLocateDate) {
                this.futureLocateDate = this.getCurrentDate(now);
                this.futureLocateTime = this.getCurrentTime(now);
            }
            // filter centerResponseCodes  and set noteToExcavator to pr_excavator_text or empty string
            const centerResponseCodes = this.getSelectedTicketsCenterResponseCodes();
            let centerResponse = centerResponseCodes.filter(centerResponseCode => centerResponseCode?.response_code === rcode)[0];
            this.noteToExcavator = centerResponse ? centerResponse?.pr_excavator_text : '';
        },
        validateUrl(v) {
            let regExString = this.urlRegEx;
            if(!this.requireResponseUrl) {
                regExString = '^$|' + regExString;
            }
            return new RegExp(regExString, 'i').test(v) || 'URL invalid.'; 
        },
        clearAllFields() {
            this.selectedMembers = [];
            this.selectedResponse = '';
            this.responseComment = '';
            this.responseUrl = '';
            this.selectResponseCircumstance = '';
            this.meetName = '';
            this.meetPhone = '';
            this.meetDate = null;
            this.meetTime = '';
            this.meetLocatorName = '';
            this.futureLocateDate = null;
            this.futureLocateTime = '';
            this.attemptContactName = '';
            this.attemptContactDate = null;
            this.attemptContactTime = '';
            this.selectAttemptContactMethod = '';
            this.attemptContactPhoneOrEmail = '';
            this.locatorName = '';
            this.locatorPhoneOrEmail = '';
            this.proposedCompletionDate = null;
            this.proposedCompletionTime = '';
            this.markedAreaDescription = '';
            this.locateDate = null;
            this.locateTime = '';
            this.unitsMarked = '';
            this.timeWorked = '';
            this.workDone = '';
            this.inhouseComments = '';
            this.noteToExcavator = '';
            this.excavRepName = '';
            this.excavProposedDueDate = null;
            this.excavDisccusedDate = null;
            this.excavDisccusedTime = '';
            this.attachmentMode = 'perTarget';
            this.pendingFilesShared = [];
            this.pendingFilesByTarget = {};
            this.uploadedRowidsByTarget = {};
            this.uploadedFilesByTarget = {};
            this.availableFilesByTarget = {};
            this.uploadErrorsByTarget = {};
            this.attachmentValidationError = '';
        },
        async addBillingCodeClick() {
            this.addBillingCode = true;
            this.billingCode = '';
            this.billingCodeMinutes = 0;
        },
        SaveBillingCodes(billingCodes){
            this.billing = billingCodes ? billingCodes : [];
        },
        buildAttachmentTargetKey(tid, memberCode) {
            return `${guidNormal(tid)}:${memberCode}`;
        },
        formatAttachmentTargetLabel(target) {
            return target.revision ? `${target.ticket}-${target.revision}` : target.ticket;
        },
        getUploadedRowidCount(targetKey) {
            return (this.uploadedRowidsByTarget[targetKey] || []).length;
        },
        getUploadedRowidsCsv(targetKey) {
            return (this.uploadedRowidsByTarget[targetKey] || []).join(',');
        },
        clearPendingAttachmentSelections() {
            this.pendingFilesShared = [];
            this.pendingFilesByTarget = {};
            this.attachmentValidationError = '';
        },
        hasPendingFiles(targetKey) {
            return (this.pendingFilesByTarget[targetKey] || []).length > 0;
        },
        async setPendingFilesForTarget(targetKey, files) {
            const normalizedFiles = this.normalizeSelectedFiles(files);
            const validationError = this.validatePendingFiles(normalizedFiles, 0);
            if (validationError) {
                this.attachmentValidationError = validationError;
                return;
            }
            try {
                const preparedFiles = await this.prepareSelectedFiles(normalizedFiles);
                this.clearUploadError(targetKey);
                this.attachmentValidationError = '';
                this.pendingFilesByTarget = {
                    ...this.pendingFilesByTarget,
                    [targetKey]: preparedFiles,
                };
            } catch (error) {
                this.setUploadError(targetKey, error?.message || 'Unable to prepare the selected attachment.');
            }
        },
        normalizeSelectedFiles(files) {
            if (!files) {
                return [];
            }
            return Array.isArray(files) ? files : [files];
        },
        async prepareSelectedFiles(files) {
            const normalizedFiles = this.normalizeSelectedFiles(files);
            return Promise.all(normalizedFiles.map(file => this.prepareSelectedFile(file)));
        },
        async prepareSelectedFile(file) {
            if (!(file instanceof File)) {
                return file;
            }

            // Read the file bytes immediately so the eventual upload does not depend
            // on a browser-managed File handle that can later be invalidated.
            const bytes = await file.arrayBuffer();
            return {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
                blob: new Blob([bytes], {
                    type: file.type || 'application/octet-stream',
                }),
            };
        },
        getUploadedFiles(targetKey) {
            return this.uploadedFilesByTarget[targetKey] || [];
        },
        getAvailableFiles(targetKey) {
            return this.availableFilesByTarget[targetKey] || [];
        },
        isExistingAttachmentSelected(targetKey, rowid) {
            return (this.uploadedRowidsByTarget[targetKey] || []).some(currentRowid => guidEqual(currentRowid, rowid));
        },
        async uploadSharedAttachments() {
            const validationError = this.validatePendingFiles(this.pendingFilesShared, this.selectedTargets.length);
            if (validationError) {
                this.attachmentValidationError = validationError;
                return;
            }
            this.clearUploadErrors();
            const hadFailure = await this.uploadAttachmentsForTargets(this.selectedTargets, this.pendingFilesShared);
            if (!hadFailure) {
                this.pendingFilesShared = [];
            }
        },
        async uploadTargetAttachments(target) {
            const pendingFiles = this.pendingFilesByTarget[target.targetKey] || [];
            const validationError = this.validatePendingFiles(pendingFiles, 1);
            if (validationError) {
                this.attachmentValidationError = validationError;
                return;
            }
            this.clearUploadError(target.targetKey);
            const hadFailure = await this.uploadAttachmentsForTargets([target], pendingFiles);
            if (!hadFailure) {
                this.pendingFilesByTarget = {
                    ...this.pendingFilesByTarget,
                    [target.targetKey]: [],
                };
            }
        },
        validatePendingFiles(files, targetCount) {
            const normalizedFiles = this.normalizeSelectedFiles(files);
            if (!normalizedFiles.length) {
                return '';
            }

            if (normalizedFiles.length > this.maxFilesPerTarget) {
                return `You can upload at most ${this.maxFilesPerTarget} files per response target.`;
            }

            const totalUploadsRequested = normalizedFiles.length * Math.max(targetCount, 1);
            if (totalUploadsRequested > this.maxFilesPerDialogSession) {
                return `This response is limited to ${this.maxFilesPerDialogSession} uploaded files per dialog session.`;
            }

            const oversizedFile = normalizedFiles.find(file => file.size > this.maxAttachmentSize);
            if (oversizedFile) {
                return `Attachment size must be less than ${Math.round(this.maxAttachmentSize / (1024 * 1024))} MB.`;
            }

            return '';
        },
        async uploadAttachmentsForTargets(targets, files) {
            if (!targets.length || !files.length) {
                return false;
            }

            this.isUploadingAttachments = true;
            this.attachmentValidationError = '';
            let hadFailure = false;
            for (const target of targets) {
                this.clearUploadError(target.targetKey);
                try {
                    for (let index = 0; index < files.length; index++) {
                        const file = files[index];
                        const uploadResult = await this.uploadAttachmentFile(target, file, index, files.length);
                        const uploadedFile = this.buildUploadedFileSummary(uploadResult, target, file);
                        this.mergeUploadedFile(target.targetKey, uploadedFile);
                    }
                    await this.refreshUploadedFilesForTarget(target);
                } catch (error) {
                    hadFailure = true;
                    this.setUploadError(target.targetKey, error?.message || 'Attachment upload failed.');
                }
            }
            if (hadFailure) {
                this.attachmentValidationError = 'Some attachment uploads failed. Fix the failed targets and retry.';
            }
            this.isUploadingAttachments = false;
            return hadFailure;
        },
        async uploadAttachmentFile(target, file, index, fileCount) {
            const formData = new FormData();
            formData.append('tid', target.tid);
            formData.append('account', this.$store.getters.user.account);
            formData.append('ticket', target.ticket);
            formData.append('revision', target.revision);
            formData.append('caption', this.buildAttachmentCaption(file, index, fileCount));
            formData.append('comment', '');
            formData.append('code', target.member_code);
            formData.append('visible', 'public');
            formData.append('file', file.blob || file, file.name);

            const result = await webtmsBackend.uploadImage(formData);
            if (result?.err) {
                throw new Error(result.err);
            }
            if(result.length > 0) {
                this.showResponseAttachmentsWarning = false;
            }
            return result;
        },
        buildAttachmentCaption(file, index, fileCount) {
            if (fileCount > 1) {
                return `${file.name} (${index + 1}/${fileCount})`;
            }
            return file.name;
        },
        buildUploadedFileSummary(uploadResult, target, file) {
            if (!uploadResult?.rowid) {
                throw new Error('Attachment upload did not return a rowid.');
            }
            return {
                rowid: guidNormal(uploadResult.rowid),
                tid: target.tid,
                member_code: target.member_code,
                file_name: uploadResult.file_name || file.name,
                caption: uploadResult.caption || file.name,
            };
        },
        mergeUploadedFile(targetKey, uploadedFile) {
            const currentRowids = this.uploadedRowidsByTarget[targetKey] || [];
            const currentFiles = this.uploadedFilesByTarget[targetKey] || [];
            if (!currentRowids.includes(uploadedFile.rowid)) {
                this.uploadedRowidsByTarget = {
                    ...this.uploadedRowidsByTarget,
                    [targetKey]: [...currentRowids, uploadedFile.rowid],
                };
            }
            if (!currentFiles.find(file => guidEqual(file.rowid, uploadedFile.rowid))) {
                this.uploadedFilesByTarget = {
                    ...this.uploadedFilesByTarget,
                    [targetKey]: [...currentFiles, uploadedFile],
                };
            }
        },
        normalizeTicketImagesResponse(ticketImagesResponse) {
            if (ticketImagesResponse && typeof ticketImagesResponse.err === 'string' && ticketImagesResponse.err.length) {
                throw new Error(ticketImagesResponse.err);
            }

            return Array.isArray(ticketImagesResponse)
                ? ticketImagesResponse
                : Array.isArray(ticketImagesResponse?.Ticket_Images)
                    ? ticketImagesResponse.Ticket_Images
                    : [];
        },
        async getTargetTicketImages(target) {
            const ticketImagesResponse = await webtmsBackend.getTicketImagesByTicket(target.ticket);
            return this.normalizeTicketImagesResponse(ticketImagesResponse)
                .filter(file => {
                    const fileMemberCode = file.member_code || file.code || '';
                    return guidEqual(file.tid, target.tid)
                        && fileMemberCode === target.member_code;
                })
                .map(file => ({
                    rowid: guidNormal(file.rowid),
                    tid: target.tid,
                    member_code: target.member_code,
                    file_name: file.file_name,
                    caption: file.caption,
                }));
        },
        async refreshUploadedFilesForTarget(target) {
            const rowids = this.uploadedRowidsByTarget[target.targetKey] || [];
            const ticketImages = await this.getTargetTicketImages(target);
            const uploadedFiles = ticketImages.filter(file => {
                return rowids.some(rowid => guidEqual(rowid, file.rowid));
            });

            this.availableFilesByTarget = {
                ...this.availableFilesByTarget,
                [target.targetKey]: ticketImages,
            };

            this.uploadedFilesByTarget = {
                ...this.uploadedFilesByTarget,
                [target.targetKey]: uploadedFiles,
            };
        },
        async refreshSelectedTargetAttachments() {
            for (const target of this.selectedTargets) {
                await this.refreshUploadedFilesForTarget(target);
            }
        },
        toggleExistingAttachment(target, file) {
            if (this.isExistingAttachmentSelected(target.targetKey, file.rowid)) {
                this.removeUploadedFile(target.targetKey, file.rowid);
                return;
            }

            this.clearUploadError(target.targetKey);
            this.attachmentValidationError = '';
            this.mergeUploadedFile(target.targetKey, file);
        },
        removeUploadedFile(targetKey, rowid) {
            this.uploadedRowidsByTarget = {
                ...this.uploadedRowidsByTarget,
                [targetKey]: (this.uploadedRowidsByTarget[targetKey] || []).filter(currentRowid => !guidEqual(currentRowid, rowid)),
            };
            this.uploadedFilesByTarget = {
                ...this.uploadedFilesByTarget,
                [targetKey]: (this.uploadedFilesByTarget[targetKey] || []).filter(file => !guidEqual(file.rowid, rowid)),
            };
        },
        setUploadError(targetKey, message) {
            this.uploadErrorsByTarget = {
                ...this.uploadErrorsByTarget,
                [targetKey]: message,
            };
        },
        clearUploadError(targetKey) {
            if (!this.uploadErrorsByTarget[targetKey]) {
                return;
            }
            const nextErrors = {
                ...this.uploadErrorsByTarget,
            };
            delete nextErrors[targetKey];
            this.uploadErrorsByTarget = nextErrors;
        },
        clearUploadErrors() {
            this.uploadErrorsByTarget = {};
        },
    }
};
</script>
<style>
#response-dialog .response-info-box {
    text-align: center;
    height: 40px;
    font-size: 14px;
    background-color: #F5F7FA;
    line-height: 40px;
    font-weight: 500;
    color: #35383F;
}
#response-dialog .member-table-label {
    font-size: 14px;
    color: #35383F;
    font-weight: 500;
}
#response-dialog .v-data-table {
    border: 1px solid #ccc;
    width: 100%;
    overflow: hidden;
}
#response-dialog.v-data-table__wrapper {
    border-radius: 4px;
}
#response-dialog thead,
#response-dialog th {
    background-color: #F5F7FA;
}
#locate-date {
    width: 200px;
}
.response-select-item {
    max-width: 410px;
    padding-bottom: 8px;
}
</style>
